import type {
	LoginFlow,
	RecoveryFlow,
	RegistrationFlow,
	SettingsFlow,
	UiNode,
	UiTextTypeEnum,
	UpdateLoginFlowBody,
	UpdateRecoveryFlowBody,
	UpdateRegistrationFlowBody,
	UpdateSettingsFlowBody,
	UpdateVerificationFlowBody,
	VerificationFlow,
} from "@ory/kratos-client";
import type React from "react";
import {
	Component,
	type FormEvent,
	type MouseEvent,
	type ReactNode,
} from "react";
import { matchFlowQuery } from "./helpers";
import {
	getNodeId,
	getNodeLabel,
	isUiNodeAnchorAttributes,
	isUiNodeImageAttributes,
	isUiNodeInputAttributes,
} from "./integrations/ui";
import { Messages } from "./Messages";
import { Node } from "./Node";

export type Values = Partial<
	| UpdateLoginFlowBody
	| UpdateRegistrationFlowBody
	| UpdateRecoveryFlowBody
	| UpdateSettingsFlowBody
	| UpdateVerificationFlowBody
>;

export type Methods =
	| "oidc"
	| "password"
	| "profile"
	| "totp"
	| "webauthn"
	| "link"
	| "code"
	| "lookup_secret";

export type Props<T> = {
	// The flow
	flow?:
		| LoginFlow
		| RegistrationFlow
		| SettingsFlow
		| VerificationFlow
		| RecoveryFlow;
	// Only show certain nodes. We will always render the default nodes for CSRF tokens.
	only?: Methods;
	// Is triggered on submission
	onSubmit: (values: T) => Promise<void>;
	// Do not show the global messages. Useful when rendering them elsewhere.
	hideGlobalMessagesTypes?: UiTextTypeEnum[];
	// Set default value for matched node
	setInputDefaultValues?: ValueInjector<string>[];
	// Set label text for matched node
	setLabelTexts?: ValueInjector<string>[];
	// Set label type for matched node
	setLabelTypes?: ValueInjector<UiTextTypeEnum>[];
	// Sort UiNodes
	sortComponents?: NodeQuery[];
	// Put components before the query matched node
	beforeComponents?: FlowNodeCustomizer[];
	// Put components after the query matched node
	afterComponents?: FlowNodeCustomizer[];
	// Put components beside of the query matched node
	besideComponents?: FlowNodeCustomizer[];
	// disable if matched components are empty
	nodeDisablers?: NodeDisabler<T>[];
	// Disabled form
	disabled?: boolean;

	context?: FlowContext;
};

export type FlowContext = {
	withIcon: boolean;
};

const defaultContext: FlowContext = {
	withIcon: false,
};

export type ValueInjector<T> = {
	query: NodeQuery;
	value: T;
};

export type FlowNodeCustomizer = {
	query: NodeQuery;
	component: React.FC;
};

export type NodeDisabler<T> = {
	target: NodeQuery;
	keys: (keyof T)[];
	allowEmpty: boolean;
	allowNotChanged: boolean;
	// if true, the node will be disabled
	additionalConditionFunc?: () => boolean;
	validators?: InputValidator<T>[];
};

export type InputValidator<T> = {
	key: keyof T;
	validate: (value: T[keyof T]) => boolean;
};

export type NodeQuery = { [key: string]: string };

function emptyState<T>() {
	return {} as T;
}

type State<T> = {
	originalValues: T;
	values: T;
	isLoading: boolean;
};

const NodeWithBeforeAfter: React.FC<{
	before: ReactNode;
	after: ReactNode;
	node: ReactNode;
}> = ({ before, after, node }) => (
	<>
		{before}
		{node}
		{after}
	</>
);

export class Flow<T extends Values> extends Component<Props<T>, State<T>> {
	constructor(props: Props<T>) {
		super(props);
		this.state = {
			originalValues: emptyState(),
			values: emptyState(),
			isLoading: false,
		};
	}

	componentDidMount() {
		this.initializeValues(this.filterNodes());
	}

	componentDidUpdate(prevProps: Props<T>) {
		if (prevProps.flow !== this.props.flow) {
			// Flow has changed, reload the values!
			this.initializeValues(this.filterNodes());
		}
	}

	initializeValues = (nodes: Array<UiNode> = []) => {
		// Compute the values
		const values = emptyState<T>();
		for (const node of nodes) {
			// This only makes sense for text nodes
			if (isUiNodeInputAttributes(node.attributes)) {
				if (
					node.attributes.type === "button" ||
					node.attributes.type === "submit"
				) {
					// In order to mimic real HTML forms, we need to skip setting the value
					// for buttons as the button value will (in normal HTML forms) only trigger
					// if the user clicks it.
					continue;
				}
				const value =
					node.attributes.value ??
					this.props.setInputDefaultValues?.find((c) =>
						matchFlowQuery(node, c.query),
					)?.value;

				values[node.attributes.name as keyof Values] = value;
			}
		}
		// Set all the values!
		this.setState((state) => ({ ...state, values, originalValues: values }));
	};

	filterNodes = (): Array<UiNode> => {
		const { flow, only } = this.props;
		if (!flow) {
			return [];
		}
		return flow.ui.nodes.filter(({ group }) => {
			if (!only) {
				return true;
			}
			return group === "default" || group === only;
		});
	};

	injectLabelTexts = (nodes: Array<UiNode>): Array<UiNode> => {
		return nodes.map((node) => {
			const setters = this.props.setLabelTexts ?? [];
			const setter = setters.find((s) => matchFlowQuery(node, s.query));

			if (setter) {
				if (isUiNodeAnchorAttributes(node.attributes)) {
					node.attributes.title.text = setter.value;
				}

				if (isUiNodeImageAttributes(node.attributes)) {
					if (node.meta.label) {
						node.meta.label.text = setter.value;
					}
				}

				if (isUiNodeInputAttributes(node.attributes)) {
					if (node.attributes.label) {
						node.attributes.label.text = setter.value;
					}
					if (node.meta.label) {
						node.meta.label.text = setter.value;
					}
				}

				if (getNodeLabel(node) === "") {
					if (node.meta.label) {
						node.meta.label.text = setter.value;
					}
				}
			}
			return node;
		});
	};

	injectLabelTypes = (nodes: Array<UiNode>): Array<UiNode> => {
		return nodes.map((node) => {
			const setters = this.props.setLabelTypes ?? [];
			const setter = setters.find((s) => matchFlowQuery(node, s.query));

			if (setter) {
				if (node.meta.label) {
					node.meta.label.type = setter.value;
				}
			}
			return node;
		});
	};

	sortNodes = (nodes: Array<UiNode>): Array<UiNode> => {
		const sorts = this.props.sortComponents ?? [];
		return nodes.sort((a, b): number => {
			const aq = sorts.find((q) => matchFlowQuery(a, q));
			const bq = sorts.find((q) => matchFlowQuery(b, q));

			if (aq === undefined || bq === undefined) {
				if (aq === undefined && bq === undefined) return 0;
				return aq === undefined ? 1 : -1;
			}
			return sorts.indexOf(aq) - sorts.indexOf(bq);
		});
	};

	// Handles form submission
	handleSubmit = async (event: FormEvent<HTMLFormElement> | MouseEvent) => {
		// Prevent all native handlers
		event.stopPropagation();
		event.preventDefault();

		// Prevent double submission!
		if (this.state.isLoading) {
			return Promise.resolve();
		}

		const form = event.currentTarget;

		let body: T | undefined;

		if (form && form instanceof HTMLFormElement) {
			const formData = new FormData(form);

			// map the entire form data to JSON for the request body
			body = Object.fromEntries(formData) as T;

			// biome-ignore  lint/suspicious/noExplicitAny : respect ory implementation
			const hasSubmitter = (evt: any): evt is { submitter: HTMLInputElement } =>
				"submitter" in evt && evt.submitter !== null;

			// We need the method specified from the name and value of the submit button.
			// when multiple submit buttons are present, the clicked one's value is used.
			if (hasSubmitter(event.nativeEvent)) {
				const method = event.nativeEvent.submitter;
				body = {
					...body,
					...{ [method.name]: method.value },
				};
			}
		}

		this.setState((state) => ({
			...state,
			isLoading: true,
		}));

		return this.props
			.onSubmit({ ...body, ...this.state.values })
			.finally(() => {
				// We wait for reconciliation and update the state after 50ms
				// Done submitting - update loading status
				this.setState((state) => ({
					...state,
					isLoading: false,
				}));
			});
	};

	/**
	 * Target Node should be disabled along with following conditions
	 * * One of value is empty
	 * * All values are not changed
	 * @param node
	 */
	isDisabled(node: UiNode): boolean {
		if (this.state.isLoading || this.props.disabled) return true;

		const disablers = this.props.nodeDisablers ?? [];
		const disabler = disablers.find((s) => matchFlowQuery(node, s.target));
		if (!disabler) return false;

		if (disabler.additionalConditionFunc) {
			if (disabler.additionalConditionFunc()) return true;
		}

		const validators = disabler.validators ?? [];
		const valid = validators.every((v) => {
			const value = this.state.values[v.key];
			return v.validate(value);
		});
		if (!valid) return true;

		const allPresent =
			disabler.allowEmpty ||
			disabler.keys.every((key) => !!this.state.values[key]);
		const someValuesAreChanged =
			disabler.allowNotChanged ||
			disabler.keys.some(
				(key) => this.state.values[key] !== this.state.originalValues[key],
			);
		return !allPresent || !someValuesAreChanged;
	}

	render() {
		const { hideGlobalMessagesTypes, flow, context } = this.props;
		const { values } = this.state;

		// Filter the nodes - only show the ones we want
		let nodes = this.filterNodes();
		nodes = this.injectLabelTexts(nodes);
		nodes = this.injectLabelTypes(nodes);
		nodes = this.sortNodes(nodes);

		if (!flow) {
			// No flow was set yet? It's probably still loading...
			//
			// Nodes have only one element? It is probably just the CSRF Token
			// and the filter did not match any elements!
			return null;
		}

		return (
			<form
				className="form-control"
				action={flow.ui.action}
				method={flow.ui.method}
				onSubmit={this.handleSubmit}
			>
				<Messages
					messages={flow.ui.messages}
					hideTypes={hideGlobalMessagesTypes ?? []}
					key={`${flow.id}-message`}
				/>
				{nodes.map((node, k) => {
					const id = getNodeId(node) as keyof Values;
					const before =
						this.props.beforeComponents?.filter((c) =>
							matchFlowQuery(node, c.query),
						) ?? [];
					const after =
						this.props.afterComponents?.filter((c) =>
							matchFlowQuery(node, c.query),
						) ?? [];
					const besides =
						this.props.besideComponents?.filter((c) =>
							matchFlowQuery(node, c.query),
						) ?? [];

					const disallowEmpty = this.props.nodeDisablers
						?.filter((d) => !d.allowEmpty)
						.flatMap((d) => d.keys)
						.some((key) => matchFlowQuery(node, { name: String(key) }));

					let validate = this.props.nodeDisablers
						?.flatMap((s) => s.validators ?? [])
						.find((s) =>
							matchFlowQuery(node, { name: String(s.key) }),
						)?.validate;
					if (!validate && disallowEmpty) {
						validate = (value): boolean => {
							if (typeof value === "string") return value.length > 0;
							return true;
						};
					}

					let res = (
						<NodeWithBeforeAfter
							// biome-ignore lint/suspicious/noArrayIndexKey: suspend unnecessary re-rendering
							key={`${id}-${k}`}
							before={before.map((c) => (
								<c.component key={`${id}-before-${JSON.stringify(c.query)}`} />
							))}
							node={
								<Node
									disabled={this.isDisabled(node)}
									node={node}
									value={values[id]}
									dispatchSubmit={this.handleSubmit}
									setValue={(value) =>
										new Promise((resolve) => {
											this.setState(
												(state) => ({
													...state,
													values: {
														...state.values,
														[getNodeId(node)]: value,
													},
												}),
												resolve,
											);
										})
									}
									context={context ?? defaultContext}
									validate={validate}
								/>
							}
							after={after.map((c) => (
								<c.component key={`${id}-after-${JSON.stringify(c.query)}`} />
							))}
						/>
					);

					if (besides.length > 0) {
						res = (
							<div
								className="md:flex flex-col items-center md:flex-row md:justify-between md:gap-3"
								// biome-ignore lint/suspicious/noArrayIndexKey: suspend unnecessary re-rendering
								key={`${id}-${k}-besides`}
							>
								{res}
								{besides.map((c) => (
									<c.component
										key={`${id}-besides-${JSON.stringify(c.query)}`}
									/>
								))}
							</div>
						);
					}
					return res;
				})}
			</form>
		);
	}
}
